import React, { useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import GoNext from './goNext'
import { size } from 'lodash'
import { StepContext } from './stepContext'
import { useTranslation } from 'react-i18next'
import { BookingContext } from './BookingContext'
import { isMobileOnly, isTablet } from 'react-device-detect'
import { Context } from '../Store'
import { getFeatureFlagCheckout2025 } from '../components/Checkout_Page_2025/Forms/LegacyValidations'
import { FloatingFooterAction } from '../components/Checkout_Page_2025/Forms/FloatingFooterAction'
import { Grid, makeStyles } from '@material-ui/core'
import { PropertyDetailCard } from '../components/Checkout_Page_2025/PropertyDetailCard'
import { PartnersBlock } from '../components/Checkout_Page_2025/PartnersBlock'
import { NextSteps } from '../components/Checkout_Page_2025/NextStepsBanners'
import { TermsAndConditionsBlock } from '../components/Checkout_Page_2025/TermsAndConditionsBlock'
import { GroupStepsSection } from '../components/Checkout_Page_2025/Forms/GroupStepsSection'
import { isMobile } from 'react-device-detect'
import { RatingStars } from '../components/Checkout_Page_2025/Forms/RatingStars'
import { PricingDetailCard } from '../components/Checkout_Page_2025/PricingDetailCard'

const useStyles = makeStyles((theme) => ({
  leftGreyBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '20px' : '16px',
  },
}))

const FormComponent = ({ onSubmit, steps, payload, defaultValues = {}, detailComponent, pageType }) => {
  const isNewCheckout2025Enabled = getFeatureFlagCheckout2025()

  const { incrementStep, decrementStep, canDecrement, step, setCanDecrement } = useContext(StepContext)
  const { setHasCounteroffer } = useContext(BookingContext)
  const [{ booking, property, current_step }, dispatch] = useContext(Context)
  const history = useHistory()

  const minPrice = (parseInt(property.price) / 100) * 75
  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: isNewCheckout2025Enabled
      ? undefined
      : yupResolver(
          step === 2
            ? yup.object().shape({
                ...steps[step].schema.fields,
                tenant_proposal: yup.number().required('This field is required').min(minPrice, 'min75percentage'),
              })
            : steps[step].schema,
        ),
    shouldUnregister: false,
  })
  const { handleSubmit, control, setValue, errors, formState, setError, clearErrors, watch } = methods

  useEffect(() => {
    const hasCounteroffer = payload.counteroffer_id != null
    setHasCounteroffer(hasCounteroffer)
    setCanDecrement(hasCounteroffer ? false : true)
  }, [])

  const { t } = useTranslation()
  const handleChange = (ev) => {
    setValue(ev.target.name, ev.target.value)
  }
  const handleOnChange = (name, value) => {
    setValue(name, value)
  }

  const FormContent = steps[step]?.form || steps[step]?.content
  if (!isNewCheckout2025Enabled && steps[step].finished) {
    return <FormContent bookingValues={payload} />
  }

  const styleClasses = useStyles()
  const onClickBack = () => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: current_step === 1 ? 1 : current_step - 1 })
    history.replace(`/bookings/${booking.id}/${current_step - 2}`)
  }

  const formOnSubmit = (data) => {
    if (size(errors) === 0) {
      return onSubmit(data)
    } else {
      return null
    }
  }

  const deviceMobile = isMobileOnly || isTablet

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(formOnSubmit)} className="h-full pt-16">
          <div
            className="w-full flex flex-col items-start justify-start overflow-auto"
            style={{ ...(deviceMobile && { height: '75%' }) }}
            id="form-container"
          >
            <div className="w-full flex flex-row items-start justify-evenly">
              {isNewCheckout2025Enabled ? (
                current_step > 1 && (
                  <div className="w-full lg:hidden lg:w-6/12 xl:w-5/12 px-2">
                    <span onClick={onClickBack}>{t('form.back')}</span>
                  </div>
                )
              ) : (
                <div className="w-full lg:w-6/12 xl:w-5/12 px-2">
                  {canDecrement && <span onClick={decrementStep}>{t('form.back')}</span>}
                </div>
              )}
              <div className="w-full lg:w-3/12" />
            </div>
            <div className="w-full flex flex-row items-start justify-evenly">
              {isNewCheckout2025Enabled ? (
                <>
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      style={{ backgroundColor: isMobile ? '#FFFFFF' : '#FBFBFB', boxShadow: '0rem 0.5rem 1rem rgba(0, 0, 0, 0.1)', padding: isMobile ? '1.25rem 1rem' : '4rem 3.125rem' }}
                    >
                      <section className={styleClasses.leftGreyBox}>
                        <PropertyDetailCard />
                        {/*Hide PricingDetailCard for step 3 */}
                        {(current_step !== 3 || isMobile) && <PricingDetailCard />}
                        {isMobile && (
                          <>
                            <GroupStepsSection />
                            <PartnersBlock />
                          </>
                        )}
                        {!isMobile && <RatingStars />}
                        <NextSteps />
                        {isMobile && <TermsAndConditionsBlock />}
                      </section>
                    </Grid>
                    <Grid item xs={12} md={8} p={2} bgcolor="lightgreen">
                      {!isMobile && <GroupStepsSection />}
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <div className="w-full lg:w-7/12 xl:w-7/12 flex md:pb-24">
                    <FormContent
                      control={control}
                      handleChange={handleChange}
                      errors={errors}
                      setError={setError}
                      handleOnChange={handleOnChange}
                      clearErrors={clearErrors}
                    />
                  </div>
                  <div className="lg:w-3/12 xl:w-3/12 hidden lg:flex">{detailComponent}</div>
                </>
              )}
            </div>
          </div>

          <div className="md:fixed z-10 bottom-0 left-0 w-full pb-8 md:p-0">
            {isNewCheckout2025Enabled ? (
              <FloatingFooterAction />
            ) : (
              <GoNext
                pageType={pageType}
                steps={steps}
                hasErrors={size(errors) > 0}
                isSubmitted={formState.isSubmitting}
                currentStep={step + 1}
                handleOnClick={incrementStep}
                isSubmit={step + 1 === size(steps)}
                payload={payload}
              />
            )}
          </div>
        </form>
      </FormProvider>
    </>
  )
}

export default FormComponent
